import React, { useState, useEffect } from 'react';
import { api, fetchSchoolSettings } from '../api/axios';
import { classLevelWord, formatClassLevelLabel, getMaxClassLevel } from '../utils/classLevelLabel';
import styles from './ReportCardManagement.module.css';

interface ReportCardSetting {
  id: number;
  school: string;
  form: number;
  term: number;
  year: number;
  term_start_date: string | null;
  term_end_date: string | null;
  next_term_opening: string | null;
  date_of_release: string | null;
  borders_fee: string | null;
  day_scholar_fee: string | null;
  principal_remarks: string | null;
  grading_scale: Array<{ position: number; range: string; remark?: string }> | null;
}

interface Props {
  school: string;
}

export const ReportCardManagement: React.FC<Props> = ({ school }) => {
  const [settings, setSettings] = useState<ReportCardSetting | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Form state
  const [selectedForm, setSelectedForm] = useState<number>(1);
  const [selectedTerm, setSelectedTerm] = useState<number>(1);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());

  useEffect(() => {
    fetchSchoolSettings(school).then(s => {
      setSelectedTerm(s.current_term);
      setSelectedYear(Number(s.current_academic_year));
    }).catch(() => {});
  }, [school]);
  
  // Settings form state
  const [termStartDate, setTermStartDate] = useState('');
  const [termEndDate, setTermEndDate] = useState('');
  const [nextTermOpening, setNextTermOpening] = useState('');
  const [dateOfRelease, setDateOfRelease] = useState('');
  const [bordersFee, setBordersFee] = useState('69696.00');
  const [dayScholarFee, setDayScholarFee] = useState('195900.00');
  const [principalRemarks, setPrincipalRemarks] = useState('');

  // Report generation state
  const [studentId, setStudentId] = useState('');
  const [students, setStudents] = useState<any[]>([]);

  useEffect(() => {
    loadSettings();
    loadStudents();
  }, [school, selectedForm, selectedTerm, selectedYear]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/api/report-cards/settings', {
        params: {
          school,
          form: selectedForm,
          term: selectedTerm,
          year: selectedYear,
        }
      });
      
      const data = response.data;
      setSettings(data);
      
      // Populate form fields
      setTermStartDate(data.term_start_date || '');
      setTermEndDate(data.term_end_date || '');
      setNextTermOpening(data.next_term_opening || '');
      setDateOfRelease(data.date_of_release || '');
      setBordersFee(data.borders_fee || '69696.00');
      setDayScholarFee(data.day_scholar_fee || '195900.00');
      setPrincipalRemarks(data.principal_remarks || '');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const loadStudents = async () => {
    try {
      const response = await api.get('/api/admin/users', {
        params: {
          role: 'student',
          school,
          form: selectedForm,
        }
      });
      // The API returns { users: [...] }, so we need to access response.data.users
      const studentsData = response.data?.users || response.data || [];
      const studentsArray = Array.isArray(studentsData) ? studentsData : [];
      setStudents(studentsArray);
    } catch (err) {
      console.error('Failed to load students:', err);
      setStudents([]); // Set empty array on error
    }
  };

  const handleSaveSettings = async () => {
    if (!settings) return;

    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      await api.put(
        `/api/report-cards/settings/${settings.id}`,
        {
          term_start_date: termStartDate || null,
          term_end_date: termEndDate || null,
          next_term_opening: nextTermOpening || null,
          date_of_release: dateOfRelease || null,
          borders_fee: parseFloat(bordersFee),
          day_scholar_fee: parseFloat(dayScholarFee),
          principal_remarks: principalRemarks || null,
        },
        {
          params: { school },
        }
      );

      setSuccessMessage('Settings saved successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateSingleReport = () => {
    if (!studentId) {
      setError('Please select a student');
      return;
    }

    const params = new URLSearchParams({
      mode: 'single',
      studentId,
      term: selectedTerm.toString(),
      year: selectedYear.toString(),
    });

    window.open(`/report-card-print?${params.toString()}`, '_blank');
  };

  const handleGenerateMassReports = () => {
    const params = new URLSearchParams({
      mode: 'mass',
      school,
      form: selectedForm.toString(),
      term: selectedTerm.toString(),
      year: selectedYear.toString(),
    });

    window.open(`/report-card-print?${params.toString()}`, '_blank');
  };

  return (
    <div className={styles.container}>
      <div className={styles.titleRow}>
        <h2 className={styles.title}>Report Card Management</h2>
        <div className={styles.learnMoreTrigger}>
          <button type="button" aria-label="Learn more about Report Card Management" className={styles.learnMoreButton}>?</button>
          <div className={styles.learnMoreTooltip}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>How to use this page</div>
            <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
              <li>Select the correct form, term, and year before editing settings.</li>
              <li>Save report card settings before generating single or mass reports.</li>
              <li>Use the same grading term label used during teacher grading.</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Selection Controls */}
      <div className={styles.selectionSection}>
        <div className={styles.formGroup}>
          <label>{classLevelWord(school)}:</label>
          <select 
            value={selectedForm} 
            onChange={(e) => setSelectedForm(parseInt(e.target.value))}
            className={styles.select}
          >
            {Array.from({ length: getMaxClassLevel(school) }, (_, i) => i + 1).map((level) => (
              <option key={level} value={level}>{formatClassLevelLabel(school, level)}</option>
            ))}
          </select>
        </div>

        <div className={styles.formGroup}>
          <label>Term:</label>
          <select 
            value={selectedTerm} 
            onChange={(e) => setSelectedTerm(parseInt(e.target.value))}
            className={styles.select}
          >
            <option value={1}>Term 1</option>
            <option value={2}>Term 2</option>
            <option value={3}>Term 3</option>
          </select>
        </div>

        <div className={styles.formGroup}>
          <label>Year:</label>
          <input
            type="number"
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className={styles.input}
            min="2020"
            max="2100"
          />
        </div>
      </div>

      {error && (
        <div className={styles.errorMessage}>
          {error}
        </div>
      )}

      {successMessage && (
        <div className={styles.successMessage}>
          {successMessage}
        </div>
      )}

      {/* Settings Configuration */}
      <div className={styles.settingsSection}>
        <h3 className={styles.sectionTitle}>Report Card Settings</h3>
        <p className={styles.subtitle}>
          Configure the settings for {school} {formatClassLevelLabel(school, selectedForm)} Term {selectedTerm} {selectedYear}
        </p>

        {loading ? (
          <div className={styles.loading}>Loading settings...</div>
        ) : (
          <div className={styles.settingsGrid}>
            <div className={styles.formGroup}>
              <label>Term Start Date:</label>
              <input
                type="date"
                value={termStartDate}
                onChange={(e) => setTermStartDate(e.target.value)}
                className={styles.input}
              />
            </div>

            <div className={styles.formGroup}>
              <label>Term End Date:</label>
              <input
                type="date"
                value={termEndDate}
                onChange={(e) => setTermEndDate(e.target.value)}
                className={styles.input}
              />
            </div>

            <div className={styles.formGroup}>
              <label>Next Term Opening:</label>
              <input
                type="date"
                value={nextTermOpening}
                onChange={(e) => setNextTermOpening(e.target.value)}
                className={styles.input}
              />
            </div>

            <div className={styles.formGroup}>
              <label>Date of Release:</label>
              <input
                type="date"
                value={dateOfRelease}
                onChange={(e) => setDateOfRelease(e.target.value)}
                className={styles.input}
              />
            </div>

            {/* Printed report cards prefer Fee Structure (Fees → Set Fee Structure) when rows exist for this term/year/form; these fields are fallback / overrides. */}
            <div className={styles.formGroup}>
              <label>Borders Fee (MK):</label>
              <input
                type="number"
                step="0.01"
                value={bordersFee}
                onChange={(e) => setBordersFee(e.target.value)}
                className={styles.input}
              />
            </div>

            <div className={styles.formGroup}>
              <label>Day Scholar Fee (MK):</label>
              <input
                type="number"
                step="0.01"
                value={dayScholarFee}
                onChange={(e) => setDayScholarFee(e.target.value)}
                className={styles.input}
              />
            </div>

            <div className={styles.formGroupFull}>
              <label>Principal's Remarks:</label>
              <textarea
                value={principalRemarks}
                onChange={(e) => setPrincipalRemarks(e.target.value)}
                className={styles.textarea}
                rows={4}
                placeholder="Enter principal's remarks that will appear on all report cards..."
              />
            </div>
          </div>
        )}

        <div className={styles.buttonGroup}>
          <button
            onClick={handleSaveSettings}
            disabled={saving || loading}
            className={styles.saveButton}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>

      {/* Report Generation */}
      <div className={styles.generationSection}>
        <h3 className={styles.sectionTitle}>Generate Report Cards</h3>

        <div className={styles.reportOptions}>
          {/* Single Student Report */}
          <div className={styles.reportOption}>
            <h4>Individual Report Card</h4>
            <p>Generate a report card for a single student</p>
            
            <div className={styles.formGroup}>
              <label>Select Student:</label>
              <select
                value={studentId}
                onChange={(e) => setStudentId(e.target.value)}
                className={styles.select}
              >
                <option value="">-- Select a student --</option>
                {students.map((student) => (
                  <option key={student.id} value={student.id}>
                    {student.name} ({student.student_id || student.login_id})
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={handleGenerateSingleReport}
              disabled={!studentId}
              className={styles.generateButton}
            >
              Generate Single Report
            </button>
          </div>

          {/* Mass Report Cards */}
          <div className={styles.reportOption}>
            <h4>Mass Report Cards</h4>
            <p>Generate report cards for all students in the selected form</p>
            
            <button
              onClick={handleGenerateMassReports}
              disabled={students.length === 0}
              className={styles.generateButton}
            >
              Generate All Reports ({students.length})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

